/**
 * Study-open card slot — renders one card per plugin that declared the
 * `study_open_card` slot, shown when the reader opens a study in the viewer.
 *
 * For 'triage' plugins the card shows:
 *   - vendor name + regulatory class (prominent)
 *   - zone label + verbatim action string
 *   - raw score to 2 decimals (per Interpretation Guide section 6)
 *   - "View heatmap" link if the plugin produced one
 *   - condensed verbatim disclaimer from manifest.complianceStrings
 */

import { usePluginResult, usePluginsForSlot } from '../hooks';
import type { LoadedRuntimePlugin } from '../types';

interface Props {
  studyId: string;
}

export function StudyOpenCardSlot({ studyId }: Props) {
  const plugins = usePluginsForSlot('study_open_card');
  if (plugins.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {plugins.map((p) => (
        <PluginCard key={p.manifest.id} plugin={p} studyId={studyId} />
      ))}
    </div>
  );
}

function PluginCard({
  plugin,
  studyId,
}: {
  plugin: LoadedRuntimePlugin;
  studyId: string;
}) {
  const { data, loading } = usePluginResult(plugin, studyId, 2000);
  const disclaimer =
    (plugin.manifest.complianceStrings ?? []).find((c) => c.slot === 'result_card_disclaimer')?.text;

  if (loading && !data) return <CardShell plugin={plugin} body="Analysing…" disclaimer={disclaimer} />;
  if (!data) return null;

  if (data.status === 'pending') {
    return <CardShell plugin={plugin} body="Analysing…" disclaimer={disclaimer} />;
  }
  if (data.status === 'skipped') {
    return (
      <CardShell
        plugin={plugin}
        body={`Not applicable: ${data.skipped_reason ?? 'eligibility criteria not met'}`}
        disclaimer={disclaimer}
      />
    );
  }
  if (data.status === 'error') {
    return (
      <CardShell
        plugin={plugin}
        body={`Error: ${data.error ?? 'unknown'}`}
        disclaimer={disclaimer}
        tone="error"
      />
    );
  }

  // Success. For triage plugins render the zone/score/action structure.
  if (plugin.manifest.category === 'triage' && data.zone && data.action) {
    return (
      <CardShell
        plugin={plugin}
        disclaimer={disclaimer}
        body={
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: zoneColor(data.zone) }}>
              {data.zone}
            </div>
            {typeof data.score === 'number' && (
              <div style={{ fontSize: 12, color: '#4b5563', marginTop: 2 }}>
                Score: {data.score.toFixed(2)} · {data.zone} · {data.action}
              </div>
            )}
            {!typeof data.score && (
              <div style={{ fontSize: 12, color: '#4b5563', marginTop: 2 }}>{data.action}</div>
            )}
            {data.heatmap_url && (
              <a
                href={data.heatmap_url as string}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 12, color: '#2563eb', marginTop: 6, display: 'inline-block' }}
              >
                View heatmap →
              </a>
            )}
          </div>
        }
      />
    );
  }

  // Generic fallback for other categories.
  return <CardShell plugin={plugin} body={`Result: ${data.status}`} disclaimer={disclaimer} />;
}

function zoneColor(z: string): string {
  if (z === 'Abnormal') return '#dc2626';
  if (z === 'Uncertain') return '#f59e0b';
  if (z === 'Normal') return '#16a34a';
  return '#374151';
}

function CardShell({
  plugin,
  body,
  disclaimer,
  tone,
}: {
  plugin: LoadedRuntimePlugin;
  body: React.ReactNode;
  disclaimer?: string;
  tone?: 'error';
}) {
  const borderColor = tone === 'error' ? '#fecaca' : '#e5e7eb';
  return (
    <div
      style={{
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        background: '#fff',
        padding: 10,
        minWidth: 240,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
          {plugin.manifest.name}
        </div>
        {plugin.manifest.vendor.regulatoryClass && (
          <div style={{ fontSize: 10, color: '#6b7280' }}>
            {plugin.manifest.vendor.regulatoryClass}
          </div>
        )}
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: '#374151' }}>{body}</div>
      {disclaimer && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 6,
            borderTop: '1px dashed #e5e7eb',
            fontSize: 10,
            color: '#6b7280',
            fontStyle: 'italic',
          }}
        >
          {disclaimer}
        </div>
      )}
    </div>
  );
}
